import {
  type NamespacedMessages,
  type PresentingLocale,
} from '@mentimeter/i18n-core';
import deepmerge from 'deepmerge';
import caES from './ca-ES.json' with { type: 'json' };
import chHansZN from './ch-Hans-ZN.json' with { type: 'json' };
import csCZ from './cs-CZ.json' with { type: 'json' };
import cyGB from './cy-GB.json' with { type: 'json' };
import daDK from './da-DK.json' with { type: 'json' };
import deDE from './de-DE.json' with { type: 'json' };
import en from './en.json' with { type: 'json' };
import esES from './es-ES.json' with { type: 'json' };
import etEE from './et-EE.json' with { type: 'json' };
import fiFI from './fi-FI.json' with { type: 'json' };
import frFR from './fr-FR.json' with { type: 'json' };
import hiIn from './hi-IN.json' with { type: 'json' };
import hrHR from './hr-HR.json' with { type: 'json' };
import huHU from './hu-HU.json' with { type: 'json' };
import idID from './id-ID.json' with { type: 'json' };
import isIS from './is-IS.json' with { type: 'json' };
import itIT from './it-IT.json' with { type: 'json' };
import jaJA from './ja-JA.json' with { type: 'json' };
import ltLT from './lt-LT.json' with { type: 'json' };
import nlNL from './nl-NL.json' with { type: 'json' };
import noNO from './no-NO.json' with { type: 'json' };
import paPK from './pa-PK.json' with { type: 'json' };
import plPL from './pl-PL.json' with { type: 'json' };
import ptBR from './pt-BR.json' with { type: 'json' };
import ptPT from './pt-PT.json' with { type: 'json' };
import roRO from './ro-RO.json' with { type: 'json' };
import ruRU from './ru-RU.json' with { type: 'json' };
import slSL from './sl-SL.json' with { type: 'json' };
import srLatnRS from './sr-Latn-RS.json' with { type: 'json' };
import svSE from './sv-SE.json' with { type: 'json' };
import taIN from './ta-IN.json' with { type: 'json' };
import trTR from './tr-TR.json' with { type: 'json' };
import ukUA from './uk-UA.json' with { type: 'json' };
import caESUnsynced from './unsynced/ca-ES.json' with { type: 'json' };
import chHansZNUnsynced from './unsynced/ch-Hans-ZN.json' with { type: 'json' };
import csCZUnsynced from './unsynced/cs-CZ.json' with { type: 'json' };
import cyGBUnsynced from './unsynced/cy-GB.json' with { type: 'json' };
import daDKUnsynced from './unsynced/da-DK.json' with { type: 'json' };
import esESUnsynced from './unsynced/es-ES.json' with { type: 'json' };
import etEEUnsynced from './unsynced/et-EE.json' with { type: 'json' };
import fiFIUnsynced from './unsynced/fi-FI.json' with { type: 'json' };
import frFRUnsynced from './unsynced/fr-FR.json' with { type: 'json' };
import hiInUnsynced from './unsynced/hi-IN.json' with { type: 'json' };
import hrHRUnsynced from './unsynced/hr-HR.json' with { type: 'json' };
import huHUUnsynced from './unsynced/hu-HU.json' with { type: 'json' };
import idIDUnsynced from './unsynced/id-ID.json' with { type: 'json' };
import isISUnsynced from './unsynced/is-IS.json' with { type: 'json' };
import itITUnsynced from './unsynced/it-IT.json' with { type: 'json' };
import jaJAUnsynced from './unsynced/ja-JA.json' with { type: 'json' };
import ltLTUnsynced from './unsynced/lt-LT.json' with { type: 'json' };
import nlNLUnsynced from './unsynced/nl-NL.json' with { type: 'json' };
import noNOUnsynced from './unsynced/no-NO.json' with { type: 'json' };
import paPKUnsynced from './unsynced/pa-PK.json' with { type: 'json' };
import plPLUnsynced from './unsynced/pl-PL.json' with { type: 'json' };
import ptBRUnsynced from './unsynced/pt-BR.json' with { type: 'json' };
import ptPTUnsynced from './unsynced/pt-PT.json' with { type: 'json' };
import roROUnsynced from './unsynced/ro-RO.json' with { type: 'json' };
import ruRUUnsynced from './unsynced/ru-RU.json' with { type: 'json' };
import slSLUnsynced from './unsynced/sl-SL.json' with { type: 'json' };
import srLatnRSUnsynced from './unsynced/sr-Latn-RS.json' with { type: 'json' };
import svSEUnsynced from './unsynced/sv-SE.json' with { type: 'json' };
import taINUnsynced from './unsynced/ta-IN.json' with { type: 'json' };
import trTRUnsynced from './unsynced/tr-TR.json' with { type: 'json' };
import ukUAUnsynced from './unsynced/uk-UA.json' with { type: 'json' };
import zhCNUnsynced from './unsynced/zh-CN.json' with { type: 'json' };
import zhCN from './zh-CN.json' with { type: 'json' };

export const ROOT_KEY = 'presenting' as const;
export type RootKey = typeof ROOT_KEY;
const defaultLanguage: NamespacedMessages<RootKey> = en;

const unsynced: Omit<NamespacedMessages<PresentingLocale>, 'en' | 'de-DE'> = {
  'ca-ES': caESUnsynced,
  'ch-Hans-ZN': chHansZNUnsynced,
  'cs-CZ': csCZUnsynced,
  'cy-GB': cyGBUnsynced,
  'da-DK': daDKUnsynced,
  'es-ES': esESUnsynced,
  'et-EE': etEEUnsynced,
  'fi-FI': fiFIUnsynced,
  'fr-FR': frFRUnsynced,
  'hi-IN': hiInUnsynced,
  'hr-HR': hrHRUnsynced,
  'hu-HU': huHUUnsynced,
  'id-ID': idIDUnsynced,
  'is-IS': isISUnsynced,
  'it-IT': itITUnsynced,
  'ja-JA': jaJAUnsynced,
  'lt-LT': ltLTUnsynced,
  'nl-NL': nlNLUnsynced,
  'no-NO': noNOUnsynced,
  'pa-PK': paPKUnsynced,
  'pl-PL': plPLUnsynced,
  'pt-BR': ptBRUnsynced,
  'pt-PT': ptPTUnsynced,
  'ro-RO': roROUnsynced,
  'ru-RU': ruRUUnsynced,
  'sl-SL': slSLUnsynced,
  'sr-Latn-RS': srLatnRSUnsynced,
  'sv-SE': svSEUnsynced,
  'ta-IN': taINUnsynced,
  'tr-TR': trTRUnsynced,
  'uk-UA': ukUAUnsynced,
  'zh-CN': zhCNUnsynced,
};

const synced: NamespacedMessages<PresentingLocale> = {
  'ca-ES': caES,
  'ch-Hans-ZN': chHansZN,
  'cs-CZ': csCZ,
  'cy-GB': cyGB,
  'da-DK': daDK,
  'de-DE': deDE,
  en: defaultLanguage,
  'es-ES': esES,
  'et-EE': etEE,
  'fi-FI': fiFI,
  'fr-FR': frFR,
  'hi-IN': hiIn,
  'hr-HR': hrHR,
  'hu-HU': huHU,
  'id-ID': idID,
  'is-IS': isIS,
  'it-IT': itIT,
  'ja-JA': jaJA,
  'lt-LT': ltLT,
  'nl-NL': nlNL,
  'no-NO': noNO,
  'pa-PK': paPK,
  'pl-PL': plPL,
  'pt-BR': ptBR,
  'pt-PT': ptPT,
  'ro-RO': roRO,
  'ru-RU': ruRU,
  'sl-SL': slSL,
  'sr-Latn-RS': srLatnRS,
  'sv-SE': svSE,
  'ta-IN': taIN,
  'tr-TR': trTR,
  'uk-UA': ukUA,
  'zh-CN': zhCN,
};

export const messages = deepmerge(
  unsynced,
  synced,
) as NamespacedMessages<PresentingLocale>;
export type Messages = typeof en;
